import { useCallback, useState, useEffect, type JSX } from 'react';
import { Button, PasswordInput, Title, Text, Stack, Box, Progress, Group, Alert, Paper } from '@mantine/core';
import { IconCheck, IconX, IconAlertCircle } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { useNavigate, useParams } from 'react-router-dom';
import { normalizeOperationOutcome } from '@medplum/core';
import { OperationOutcome } from '@medplum/fhirtypes';
import jenPImage from '../img/homePage/jen-p-FoG8lotg7AA-unsplash.jpg';

function PasswordRequirement({ meets, label }: { meets: boolean; label: string }) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      {meets ? <IconCheck size={14} /> : <IconX size={14} />} <Box ml={10}>{label}</Box>
    </Text>
  );
}

function PasswordStrength({ password }: { password: string }) {
  const requirements = [
    { re: /[0-9]/, label: 'Includes number' },
    { re: /[a-z]/, label: 'Includes lowercase letter' },
    { re: /[A-Z]/, label: 'Includes uppercase letter' },
    { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol' },
  ];

  const strength = requirements.filter((requirement) => requirement.re.test(password)).length;
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(password)}
    />
  ));

  const bars = Array(4)
    .fill(0)
    .map((_, index) => (
      <Progress
        key={index}
        value={password.length > 0 && index <= strength ? 100 : 0}
        color={strength <= 1 ? 'red' : strength === 2 ? 'yellow' : strength === 3 ? 'blue' : 'green'}
        size={4}
      />
    ));

  return (
    <>
      <Group gap={5} grow mt="xs" mb="md">
        {bars}
      </Group>
      <PasswordRequirement label="Has at least 8 characters" meets={password.length >= 8} />
      {checks}
    </>
  );
}

export function SetPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const params = useParams<{ id: string; secret: string }>();
  const { id, secret } = params;
  
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [validatingToken, setValidatingToken] = useState<boolean>(true);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  
  // Validate token on component mount
  useEffect(() => {
    async function validateToken() {
      setValidatingToken(true);
      try {
        if (!id || !secret) {
          setTokenValid(false);
          setOutcome(normalizeOperationOutcome(new Error('Invalid reset password link. Please request a new one.')));
          return;
        }
        
        // This is just checking if the parameters exist
        // The actual validation happens when setPassword is called
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
        setOutcome(normalizeOperationOutcome(err));
      } finally {
        setValidatingToken(false);
      }
    }
    
    validateToken();
  }, [id, secret]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setOutcome(undefined);

      if (password !== passwordConfirmation) {
        setOutcome(normalizeOperationOutcome(new Error('Passwords do not match')));
        setLoading(false);
        return;
      }

      if (password.length < 8) {
        setOutcome(normalizeOperationOutcome(new Error('Password must be at least 8 characters')));
        setLoading(false);
        return;
      }

      try {
        if (!id || !secret) {
          throw new Error('Missing id or secret');
        }
        
        // API Call
        await medplum.post('auth/setpassword', {
          id,
          secret,
          password,
        });
        
        // Set success state
        setSuccess(true);
      } catch (err) {
        setOutcome(normalizeOperationOutcome(err));
      } finally {
        setLoading(false);
      }
    },
    [medplum, id, secret, password, passwordConfirmation]
  );

  // Content to show based on state
  let content;

  if (success) {
    content = (
      <Stack align="center" gap="md" pt={{ base: 40, md: 100 }} pb={{ base: 40, md: 200 }} px={{ base: 'md', md: 0 }}>
        <Title ta="center" fw={500} size="h2">
          Password Reset Successful
        </Title>
        <Text ta="center" maw={400}>
          Your password has been reset successfully. You can now sign in with your new password.
        </Text>
        <Button
          onClick={() => {
            // Perform a complete logout and navigate to signin
            try {
              medplum.signOut().catch(() => {});
            } catch (err) {
              // Ignore errors
            }
            // Use full URL to avoid routing issues
            window.location.replace(window.location.origin + '/signin');
          }}
          size="md"
          radius="md"
          mt="xl"
          fullWidth
          maw={300}
        >
          Sign In
        </Button>
      </Stack>
    );
  } else if (validatingToken) {
    content = (
      <Stack align="center" gap="md" pt={{ base: 40, md: 100 }} pb={{ base: 40, md: 200 }} px={{ base: 'md', md: 0 }}>
        <Title ta="center" fw={500} size="h2">
          Verifying Your Request
        </Title>
        <Text ta="center" maw={400}>
          Please wait while we verify your password reset request...
        </Text>
        <Progress value={100} size="sm" radius="xl" w={{ base: '100%', sm: 300 }} maw={300} animated />
      </Stack>
    );
  } else if (!tokenValid) {
    content = (
      <Stack align="center" gap="md" pt={{ base: 40, md: 100 }} pb={{ base: 40, md: 200 }} px={{ base: 'md', md: 0 }}>
        <Title ta="center" fw={500} size="h2">
          Invalid Reset Link
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Invalid or expired link"
          color="red"
          radius="md"
          maw={400}
          w="100%"
        >
          {outcome?.issue?.[0]?.details?.text || "This password reset link is invalid or has expired. Please request a new one."}
        </Alert>
        <Button onClick={() => navigate('/resetpassword')} size="md" radius="md" mt="xl" fullWidth maw={300}>
          Request New Reset Link
        </Button>
      </Stack>
    );
  } else {
    content = (
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Stack align="center" gap="md" pt={{ base: 40, md: 100 }} pb={{ base: 40, md: 200 }} maw={500} mx="auto" px={{ base: 'md', md: 0 }}>
          <Title ta="center" fw={500} size="h2">
            Set New Password
          </Title>
          <Text c="dimmed" size="sm" ta="center" maw={400}>
            Create a new password for your FeelHeard account
          </Text>

          <Box w="100%" maw={400}>
            {outcome && (
              <Paper p="md" mb="md" bg="red.0" radius="md">
                <Text c="red">{outcome.issue?.[0]?.details?.text || 'An error occurred while processing your request.'}</Text>
              </Paper>
            )}

            <PasswordInput
              label="New password"
              placeholder="Your new password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              mt="md"
              radius="md"
            />

            <PasswordStrength password={password} />

            <PasswordInput
              label="Confirm password"
              placeholder="Confirm your new password"
              required
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              disabled={loading}
              mt="md"
              radius="md"
            />

            <Button
              type="submit"
              fullWidth
              mt="xl"
              loading={loading}
              disabled={password.length < 8 || password !== passwordConfirmation}
              radius="md"
              size="md"
            >
              Reset Password
            </Button>
          </Box>
        </Stack>
      </form>
    );
  }

  // Responsive layout: single column on mobile, two columns on desktop
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      <div className="flex flex-col justify-center items-center">
        {content}
      </div>
      <div
        className="hidden md:block bg-cover bg-center"
        style={{ backgroundImage: `url(${jenPImage})` }}
      />
    </div>
  );
}